import React, { useCallback, useState } from "react";
import Cropper from "react-easy-crop";
import { Box, Dialog, DialogContent, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import getCroppedImg from "../../helpers/cropImage";
import SoftButton from "../../components/SoftButton";
import SoftTypography from "../../components/SoftTypography";
import { UseCompanyColors } from "../../hooks/company";
import { DEFAULT_BRAND, brandRgba, brandDarken } from "../../helpers/brandColor";

const ZoomIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="7" />
    <path d="M21 21l-4.35-4.35" />
  </svg>
);

const ImageCropper = ({ imgSrc, open, setOpen, onSaveCroppedImage }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const { primaryColor } = UseCompanyColors();
  const brand = primaryColor || DEFAULT_BRAND;
  const brandDark = brandDarken(brand, 0.18);

  const onCropComplete = useCallback((_croppedArea, pixels) => {
    setCroppedAreaPixels(pixels);
  }, []);

  const showCroppedImage = useCallback(async () => {
    try {
      const croppedImageFile = await getCroppedImg(imgSrc, croppedAreaPixels, 0);
      onSaveCroppedImage(croppedImageFile);
    } catch (e) {
      console.error(e);
    }
  }, [croppedAreaPixels, imgSrc, onSaveCroppedImage]);

  return (
    <Dialog
      onClose={() => setOpen(false)}
      open={open}
      fullWidth
      fullScreen={fullScreen}
      PaperProps={{
        style: {
          borderRadius: fullScreen ? 0 : "20px",
          overflow: "hidden",
        },
      }}
    >
      <Box sx={{ padding: "16px 20px", borderBottom: "1px solid rgba(15,23,42,0.06)" }}>
        <SoftTypography sx={{ fontSize: "16px!important", fontWeight: "700!important", color: "#0F172A" }}>
          Crop Photo
        </SoftTypography>
        <SoftTypography sx={{ fontSize: "12px!important", fontWeight: "500!important", color: "#667085" }}>
          Drag to reposition, use the slider to zoom
        </SoftTypography>
      </Box>

      <DialogContent sx={{ padding: 0 }}>
        <Box sx={{ position: "relative", height: fullScreen ? "58vh" : 420, background: "#0F172A" }}>
          <Cropper
            image={imgSrc}
            crop={crop}
            zoom={zoom}
            aspect={1}
            onCropChange={setCrop}
            onCropComplete={onCropComplete}
            onZoomChange={setZoom}
          />
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: "12px", padding: "16px 20px" }}>
          <ZoomIcon size={16} />
          <input
            type="range"
            min={1}
            max={3}
            step={0.05}
            value={zoom}
            aria-label="Zoom"
            onChange={(e) => setZoom(Number(e.target.value))}
            style={{ flex: 1, accentColor: brand, height: "20px", cursor: "pointer" }}
          />
          <ZoomIcon size={22} />
        </Box>
      </DialogContent>

      <Box
        sx={{
          display: "flex",
          gap: "12px",
          padding: "16px 20px",
          borderTop: "1px solid rgba(15,23,42,0.06)",
        }}
      >
        <SoftButton
          onClick={() => setOpen(false)}
          variant="outlined"
          color="secondary"
          fullWidth
          sx={{
            color: "#475467 !important",
            border: "1px solid rgba(15,23,42,0.12)",
            backgroundColor: "transparent",
            borderRadius: "10px",
            "&:hover": { backgroundColor: "rgba(15,23,42,0.04)" },
          }}
        >
          Cancel
        </SoftButton>
        <SoftButton
          onClick={showCroppedImage}
          variant="contained"
          fullWidth
          sx={{
            color: "#fff !important",
            border: "none",
            borderRadius: "10px",
            background: `linear-gradient(95.76deg, ${brand} -1.6%, ${brandDark} 105.5%) !important`,
            boxShadow: `0px 4px 8px ${brandRgba(brand, 0.19)}`,
            "&:hover": { background: `linear-gradient(95.76deg, ${brand} -1.6%, ${brandDark} 105.5%) !important` },
          }}
        >
          Done
        </SoftButton>
      </Box>
    </Dialog>
  );
};

export default ImageCropper;
